import { useState } from 'react';
import type { DecideResponse } from '../types';
import { api } from '../api';
import { useI18n } from '../i18n';

export function Decision() {
  const { t, locale } = useI18n();
  const [decision, setDecision] = useState('');
  const [context, setContext] = useState('');
  const [options, setOptions] = useState('');
  const [result, setResult] = useState<DecideResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!decision.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const opts = options
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const out = await api.decide(
        decision.trim(),
        context.trim() || undefined,
        opts.length ? opts : undefined,
        locale,
      );
      setResult(out);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('decision.title')}</h1>
          <div className="page-sub">{t('decision.subtitle')}</div>
        </div>
      </header>

      <section className="card">
        <form onSubmit={submit} className="stack">
          <div>
            <label className="card-title">{t('decision.label.decision')}</label>
            <textarea
              className="textarea"
              placeholder={t('decision.placeholder.decision')}
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
            />
          </div>
          <div>
            <label className="card-title">{t('decision.label.context')}</label>
            <textarea
              className="textarea"
              placeholder={t('decision.placeholder.context')}
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </div>
          <div>
            <label className="card-title">{t('decision.label.options')}</label>
            <textarea
              className="textarea"
              placeholder={t('decision.placeholder.options')}
              value={options}
              onChange={(e) => setOptions(e.target.value)}
            />
          </div>
          <div>
            <button type="submit" className="btn btn-primary" disabled={loading || !decision.trim()}>
              {loading ? t('decision.analyzing') : t('decision.analyze')}
            </button>
          </div>
          {error && <div className="error-banner">{error}</div>}
        </form>
      </section>

      {result && (
        <section className="card">
          <h2 className="card-title">{t('decision.result')}</h2>
          <div className="row-between">
            <div style={{ fontSize: 15, fontWeight: 600 }}>{result.decision}</div>
            <span className={`badge-conf ${result.confidence}`}>
              {t(`confidence.${result.confidence}`)} {t('decision.confidence')}
            </span>
          </div>

          <div className="grid-2" style={{ marginTop: 16 }}>
            <div>
              <div className="ai-section-title">{t('decision.section.pros')}</div>
              <ul className="bullet-list">
                {result.pros.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="ai-section-title">{t('decision.section.cons')}</div>
              <ul className="bullet-list">
                {result.cons.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="ai-section-title">{t('decision.section.risks')}</div>
          <ul className="bullet-list">
            {result.risks.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>

          <div className="divider" />
          <div className="ai-section-title">{t('decision.section.recommended')}</div>
          <div style={{ fontSize: 15, lineHeight: 1.55 }}>{result.recommendation}</div>
        </section>
      )}
    </>
  );
}
